import { useEffect, useState } from 'react';
import { FiDownload, FiRefreshCw, FiX } from 'react-icons/fi';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'portal-pwa-instalar-dispensado';

export function PWAPrompts() {
  const [precisaAtualizar, setPrecisaAtualizar] = useState(false);
  const [waitingSW, setWaitingSW] = useState<ServiceWorker | null>(null);
  const [promptInstalar, setPromptInstalar] = useState<BeforeInstallPromptEvent | null>(null);
  const [mostrarInstalar, setMostrarInstalar] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let cancelado = false;

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || cancelado) return;
      if (reg.waiting && navigator.serviceWorker.controller) {
        setWaitingSW(reg.waiting);
        setPrecisaAtualizar(true);
      }
      reg.addEventListener('updatefound', () => {
        const novo = reg.installing;
        if (!novo) return;
        novo.addEventListener('statechange', () => {
          if (novo.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingSW(novo);
            setPrecisaAtualizar(true);
          }
        });
      });
    });

    const onControllerChange = () => {
      if (!cancelado) window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      cancelado = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      setPromptInstalar(evt);
      const dispensadoAte = localStorage.getItem(STORAGE_KEY);
      const aindaDispensado = dispensadoAte && parseInt(dispensadoAte, 10) > Date.now();
      if (!aindaDispensado) {
        setMostrarInstalar(true);
      }
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  async function instalar() {
    if (!promptInstalar) return;
    await promptInstalar.prompt();
    await promptInstalar.userChoice;
    setPromptInstalar(null);
    setMostrarInstalar(false);
  }

  function dispensarInstalar() {
    const ate = Date.now() + 1000 * 60 * 60 * 24 * 30;
    localStorage.setItem(STORAGE_KEY, String(ate));
    setMostrarInstalar(false);
  }

  function atualizar() {
    if (waitingSW) {
      waitingSW.postMessage({ type: 'SKIP_WAITING' });
    } else {
      window.location.reload();
    }
  }

  return (
    <>
      {precisaAtualizar && (
        <div className="fixed bottom-4 right-4 z-[9999] max-w-sm bg-white border border-slate-200 rounded-lg shadow-xl p-4 flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
            <FiRefreshCw size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">Nova versão disponível</div>
            <p className="text-xs text-slate-600 mt-0.5">
              Há uma atualização do portal pronta para uso. Recarregue para aplicar.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={atualizar}
                className="px-3 py-1.5 text-xs font-medium bg-institutional text-white rounded hover:bg-institutional-dark transition"
              >
                Atualizar agora
              </button>
              <button
                onClick={() => setPrecisaAtualizar(false)}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition"
              >
                Depois
              </button>
            </div>
          </div>
          <button
            onClick={() => setPrecisaAtualizar(false)}
            className="flex-shrink-0 text-slate-400 hover:text-slate-600 p-1"
            aria-label="Fechar"
          >
            <FiX size={16} />
          </button>
        </div>
      )}

      {mostrarInstalar && promptInstalar && (
        <div className="fixed bottom-4 left-4 z-[9999] max-w-sm bg-white border border-slate-200 rounded-lg shadow-xl p-4 flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <FiDownload size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">Instalar o portal</div>
            <p className="text-xs text-slate-600 mt-0.5">
              Acesse os serviços públicos direto da tela inicial do seu celular.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={instalar}
                className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 transition"
              >
                Instalar
              </button>
              <button
                onClick={dispensarInstalar}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition"
              >
                Agora não
              </button>
            </div>
          </div>
          <button
            onClick={dispensarInstalar}
            className="flex-shrink-0 text-slate-400 hover:text-slate-600 p-1"
            aria-label="Fechar"
          >
            <FiX size={16} />
          </button>
        </div>
      )}
    </>
  );
}
